import { useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  getMe,
  updateMe,
  changePassword,
  uploadAvatar,
  deleteAvatar,
  ApiError,
  type CurrentUser,
} from "@/lib/api";
import { applyTheme, getTheme, type Theme } from "@/lib/theme";
import { Avatar } from "@/components/Avatar";

function SectionLabel({ children }: { children: React.ReactNode }) {
  return (
    <h2 className="mb-3 text-xs font-semibold uppercase tracking-wider text-text-muted">
      {children}
    </h2>
  );
}

function RoleBadge({ role }: { role: "admin" | "member" }) {
  return (
    <span
      className={`rounded-full px-2 py-0.5 text-[11px] font-medium capitalize ${
        role === "admin"
          ? "bg-accent-muted text-accent"
          : "border border-border text-text-secondary"
      }`}
    >
      {role}
    </span>
  );
}

const cardCls = "rounded-token-lg border border-border bg-bg-card p-6";
const inputCls =
  "w-full rounded-token-md border border-border bg-bg px-3 py-2 text-sm text-text outline-none transition-colors focus:border-accent focus:ring-2 focus:ring-accent/20";
const labelCls = "mb-1.5 block text-xs font-medium text-text-muted";

export function SettingsPage() {
  const { data: me } = useQuery({ queryKey: ["me"], queryFn: getMe });

  return (
    <div className="max-w-3xl">
      <header className="mb-8">
        <h1 className="page-title">Settings</h1>
        <p className="mt-2 text-text-secondary">
          Your profile, appearance, and security. Team management lives under
          Operations → Team.
        </p>
      </header>

      <section className="mb-10">
        <SectionLabel>Profile</SectionLabel>
        {me && <ProfileCard me={me} />}
      </section>

      <section className="mb-10">
        <SectionLabel>Appearance</SectionLabel>
        <ThemeSection />
      </section>

      <section className="mb-10">
        <SectionLabel>Security</SectionLabel>
        <ChangePasswordCard />
      </section>

    </div>
  );
}



/* ── Appearance ─────────────────────────────────────────────────────────── */

/* Mini app previews. These are literal hex copies of the theme tokens rather
   than var() reads, because each swatch has to paint the *other* theme's
   colours while the current one is live. Keep them in step with index.css. */
const THEME_PREVIEWS: {
  theme: Theme;
  label: string;
  blurb: string;
  bg: string;
  /** Sidebar rail: pine in light, elevated navy in dark. */
  rail: string;
  /** Brand dot at the top of the rail. */
  railDot: string;
  /** Nav-item bars drawn on the rail. */
  railInk: string;
  card: string;
  line: string;
  accent: string;
}[] = [
  {
    theme: "light",
    label: "Light",
    blurb: "Pure white, deep pine — matches the website.",
    bg: "#ffffff",
    rail: "#0e5a41",
    railDot: "#ffffff",
    railInk: "rgba(255, 255, 255, 0.45)",
    card: "#ffffff",
    line: "#e5e5e5",
    accent: "#0e5a41",
  },
  {
    theme: "dark",
    label: "Dark",
    blurb: "Deep navy with emerald accents.",
    bg: "#0a0e17",
    rail: "#111827",
    railDot: "#10b981",
    railInk: "#2a3650",
    card: "#151d2e",
    line: "#2a3650",
    accent: "#10b981",
  },
];

function ThemeSection() {
  const [theme, setTheme] = useState<Theme>(getTheme());

  function pick(next: Theme) {
    applyTheme(next);
    setTheme(next);
  }

  return (
    <div className="grid gap-4 sm:grid-cols-2">
      {THEME_PREVIEWS.map((t) => {
        const active = theme === t.theme;
        return (
          <button
            key={t.theme}
            onClick={() => pick(t.theme)}
            aria-pressed={active}
            className={`group rounded-token-lg border-2 bg-bg-card p-3 text-left transition-colors ${
              active ? "border-accent" : "border-border hover:border-accent/40"
            }`}
          >
            {/* mini app preview */}
            <div
              className="relative mb-3 h-24 overflow-hidden rounded-token-md border"
              style={{ background: t.bg, borderColor: t.line }}
            >
              <div
                className="absolute inset-y-0 left-0 w-1/4"
                style={{ background: t.rail, borderRight: `1px solid ${t.line}` }}
              >
                <span
                  className="absolute left-2 top-2 h-2 w-2 rounded-full"
                  style={{ background: t.railDot }}
                />
                <span
                  className="absolute left-2 top-6 h-1 w-8 rounded"
                  style={{ background: t.railInk }}
                />
                <span
                  className="absolute left-2 top-9 h-1 w-6 rounded"
                  style={{ background: t.railInk }}
                />
              </div>
              <div
                className="absolute right-3 top-3 h-8 w-[62%] rounded"
                style={{ background: t.card, border: `1px solid ${t.line}` }}
              />
              <div
                className="absolute bottom-3 right-3 h-6 w-[62%] rounded"
                style={{ background: t.card, border: `1px solid ${t.line}` }}
              />
              {active && (
                <span
                  className="absolute bottom-2 left-2 grid h-5 w-5 place-items-center rounded-full text-[10px] text-white"
                  style={{ background: t.accent }}
                >
                  <i className="fa-solid fa-check" />
                </span>
              )}
            </div>
            <p className="text-sm font-semibold text-text-heading">{t.label}</p>
            <p className="mt-0.5 text-xs text-text-secondary">{t.blurb}</p>
          </button>
        );
      })}
    </div>
  );
}

function ProfileCard({ me }: { me: CurrentUser }) {
  const qc = useQueryClient();
  const fileRef = useRef<HTMLInputElement>(null);
  const [error, setError] = useState<string | null>(null);

  const onUser = (u: CurrentUser) => {
    qc.setQueryData(["me"], u);
    qc.invalidateQueries({ queryKey: ["members"] });
    qc.invalidateQueries({ queryKey: ["tickets"] });
    qc.invalidateQueries({ queryKey: ["users"] });
  };

  const upload = useMutation({
    mutationFn: (file: File) => uploadAvatar(file),
    onSuccess: onUser,
    onError: (e) => setError(e instanceof ApiError ? e.message : "Upload failed"),
  });
  const remove = useMutation({ mutationFn: deleteAvatar, onSuccess: onUser });
  const busy = upload.isPending || remove.isPending;

  function onPick(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setError(null);
    if (!file.type.startsWith("image/")) {
      setError("Please choose an image file (PNG, JPG, WEBP, or GIF).");
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      setError("Image must be 5 MB or smaller.");
      return;
    }
    upload.mutate(file);
  }

  return (
    <div className={cardCls}>
      <div className="flex items-start gap-4">
        <Avatar name={me.name} url={me.avatarUrl} size={72} />
        <div className="min-w-0 flex-1">
          <EditableName me={me} onSaved={onUser} />
          {me.title && <p className="truncate text-sm text-text-secondary">{me.title}</p>}
          <p className="truncate text-sm text-text-muted">{me.email}</p>

          <div className="mt-4 flex gap-2">
            <button
              onClick={() => fileRef.current?.click()}
              disabled={busy}
              className="rounded-token-md bg-accent text-on-accent px-3 py-1.5 text-sm font-semibold transition-opacity hover:opacity-90 disabled:opacity-60"
              style={{ color: "var(--color-on-accent)" }}
            >
              {busy ? "Saving…" : me.avatarUrl ? "Change photo" : "Upload photo"}
            </button>
            {me.avatarUrl && (
              <button
                onClick={() => remove.mutate()}
                disabled={busy}
                className="rounded-token-md border border-border px-3 py-1.5 text-sm text-text-secondary transition-colors hover:text-text-heading"
              >
                Remove
              </button>
            )}
          </div>
        </div>
      </div>
      {error && (
        <p className="mt-4 rounded-token-md bg-danger/10 px-3 py-2 text-sm text-danger">{error}</p>
      )}
      <input
        ref={fileRef}
        type="file"
        accept="image/png,image/jpeg,image/webp,image/gif"
        className="hidden"
        onChange={onPick}
      />
    </div>
  );
}

/** The user's display name with click-to-edit (pencil on hover, Enter saves). */
function EditableName({
  me,
  onSaved,
}: {
  me: CurrentUser;
  onSaved: (u: CurrentUser) => void;
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(me.name);

  const save = useMutation({
    mutationFn: () => updateMe(draft.trim()),
    onSuccess: (u) => {
      onSaved(u);
      setEditing(false);
    },
  });

  if (editing) {
    return (
      <form
        className="flex items-center gap-2"
        onSubmit={(e) => {
          e.preventDefault();
          if (draft.trim() && draft.trim() !== me.name) save.mutate();
          else setEditing(false);
        }}
      >
        <input
          autoFocus
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => e.key === "Escape" && setEditing(false)}
          className="w-52 rounded-token-md border border-border bg-bg px-2 py-1 font-display text-lg font-semibold text-text-heading outline-none focus:border-accent"
          maxLength={120}
          required
        />
        <button
          type="submit"
          disabled={save.isPending}
          title="Save name"
          className="grid h-7 w-7 place-items-center rounded-md bg-accent text-on-accent text-xs disabled:opacity-50"
          style={{ color: "var(--color-on-accent)" }}
        >
          <i className={`fa-solid ${save.isPending ? "fa-circle-notch fa-spin" : "fa-check"}`} />
        </button>
        <button
          type="button"
          onClick={() => {
            setDraft(me.name);
            setEditing(false);
          }}
          title="Cancel"
          className="grid h-7 w-7 place-items-center rounded-md text-xs text-text-muted hover:bg-bg-card-hover hover:text-text-heading"
        >
          <i className="fa-solid fa-xmark" />
        </button>
      </form>
    );
  }

  return (
    <div className="group flex items-center gap-2">
      <h3 className="truncate font-display text-lg font-semibold text-text-heading">
        {me.name}
      </h3>
      <RoleBadge role={me.role} />
      <button
        onClick={() => {
          setDraft(me.name);
          setEditing(true);
        }}
        title="Edit your name"
        className="grid h-6 w-6 place-items-center rounded text-xs text-text-muted opacity-0 transition-opacity hover:bg-bg-card-hover hover:text-text-heading group-hover:opacity-100"
      >
        <i className="fa-solid fa-pen" />
      </button>
    </div>
  );
}

function ChangePasswordCard() {
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);

  const mutate = useMutation({
    mutationFn: () => changePassword(current, next),
    onSuccess: () => {
      setCurrent("");
      setNext("");
      setConfirm("");
      setError(null);
      setDone(true);
    },
    onError: (e) => setError(e instanceof ApiError ? e.message : "Could not change password"),
  });

  function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setDone(false);
    setError(null);
    if (next !== confirm) {
      setError("New passwords do not match");
      return;
    }
    mutate.mutate();
  }

  return (
    <form onSubmit={onSubmit} className={cardCls}>
      <h3 className="mb-4 font-display text-lg font-semibold text-text-heading">Change password</h3>

      <div className="mb-3">
        <label className={labelCls}>Current password</label>
        <input
          type="password"
          autoComplete="current-password"
          className={inputCls}
          value={current}
          onChange={(e) => setCurrent(e.target.value)}
          required
        />
      </div>
      <div className="mb-3">
        <label className={labelCls}>New password (min 8 chars)</label>
        <input
          type="password"
          autoComplete="new-password"
          className={inputCls}
          value={next}
          onChange={(e) => setNext(e.target.value)}
          minLength={8}
          required
        />
      </div>
      <div className="mb-4">
        <label className={labelCls}>Confirm new password</label>
        <input
          type="password"
          autoComplete="new-password"
          className={inputCls}
          value={confirm}
          onChange={(e) => setConfirm(e.target.value)}
          minLength={8}
          required
        />
      </div>

      {error && (
        <p className="mb-3 rounded-token-md bg-danger/10 px-3 py-2 text-sm text-danger">{error}</p>
      )}
      {done && (
        <p className="mb-3 rounded-token-md bg-accent-muted px-3 py-2 text-sm text-accent">
          Password updated.
        </p>
      )}

      <button
        type="submit"
        disabled={mutate.isPending}
        className="rounded-token-md bg-accent text-on-accent px-4 py-2 text-sm font-semibold transition-opacity hover:opacity-90 disabled:opacity-60"
        style={{ color: "var(--color-on-accent)" }}
      >
        {mutate.isPending ? "Updating…" : "Update password"}
      </button>
    </form>
  );
}
